import Hoverable from '@omni/kit/components/Hoverable';
import KitIcon from '@omni/kit/components/KitIcon';
import { IconName } from '@omni/kit/components/KitIcon';
import Tooltip from '@omni/kit/components/Tooltip';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import { transparentHexColor } from '@omni/kit/utilities/utilities';
import React, {
  ForwardRefExoticComponent,
  ReactElement,
  RefAttributes,
  useRef,
  useState,
} from 'react';
import {
  Pressable,
  PressableProps,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

import { PlayerButtonLockFocusType } from '../../types';

/**
 * Creates a copy of Pressable component that allows for passing in TV props.
 */
interface TVPressableProps extends PressableProps {
  nextFocusUp?: unknown | null;
  nextFocusDown?: unknown | null;
  nextFocusLeft?: unknown | null;
  nextFocusRight?: unknown | null;
}

const TVPressable = Pressable as ForwardRefExoticComponent<
  TVPressableProps & RefAttributes<View>
>;

export enum PlayerButtonSizes {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

const iconSizeMap = {
  [PlayerButtonSizes.small]: 12,
  [PlayerButtonSizes.medium]: 20,
  [PlayerButtonSizes.large]: 24,
};

const touchableOffsetMap = {
  [PlayerButtonSizes.small]: 18,
  [PlayerButtonSizes.medium]: 24,
  [PlayerButtonSizes.large]: 30,
};

const HoverColor = transparentHexColor(Colors.N0, 20);
const PressColor = transparentHexColor(Colors.N0, 35);
const FocusColor = transparentHexColor(Colors.N0, 90);

export interface PlayerButtonProps {
  title: string;
  icon: IconName;
  onPress: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  size?: PlayerButtonSizes;
  hasTVPreferredFocus?: boolean;
  trapFocusDirections?: PlayerButtonLockFocusType[];
  style?: ViewStyle;
}

export default function PlayerButton({
  title,
  icon,
  onPress,
  onFocus,
  onBlur,
  size = PlayerButtonSizes.large,
  hasTVPreferredFocus,
  trapFocusDirections,
  style = {},
}: PlayerButtonProps): ReactElement {
  const playerButtonRef = useRef<View>(null);

  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const iconSize = iconSizeMap[size];
  const touchableOffset = touchableOffsetMap[size];

  let lockFocusProps: TVPressableProps = {};

  if (trapFocusDirections?.length) {
    lockFocusProps = {
      nextFocusUp: trapFocusDirections?.includes('up')
        ? playerButtonRef?.current
        : null,
      nextFocusDown: trapFocusDirections?.includes('down')
        ? playerButtonRef?.current
        : null,
      nextFocusLeft: trapFocusDirections?.includes('left')
        ? playerButtonRef?.current
        : null,
      nextFocusRight: trapFocusDirections?.includes('right')
        ? playerButtonRef?.current
        : null,
    };
  }

  return (
    <Tooltip label={title} offsetY={touchableOffset / 2}>
      <Hoverable
        onHoverIn={() => setIsHovering(true)}
        onHoverOut={() => setIsHovering(false)}
      >
        <View style={style}>
          <TVPressable
            ref={playerButtonRef}
            hasTVPreferredFocus={hasTVPreferredFocus}
            onPress={onPress}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.();
            }}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.();
            }}
            style={(state) => [
              styles.button,
              isHovering && styles.buttonHovered,
              state.pressed && styles.buttonPressed,
              isFocused && styles.buttonFocused,
              {
                height: iconSize + touchableOffset,
                width: iconSize + touchableOffset,
                margin: 0,
              },
            ]}
            {...lockFocusProps}
          >
            <KitIcon
              name={icon}
              color={isFocused ? Colors.N1000 : Colors.N0}
              size={iconSize}
            />
          </TVPressable>
        </View>
      </Hoverable>
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  button: {
    borderRadius: BorderRadius.full,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonHovered: {
    backgroundColor: HoverColor,
  },
  buttonPressed: {
    backgroundColor: PressColor,
    transform: [{ scale: 1 }],
  },
  buttonFocused: {
    backgroundColor: FocusColor,
  },
});
